import { useEffect } from 'react'
import { Keyboard, View } from 'react-native'
import { useMutation, useQuery } from '@apollo/client'
import { useController, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { SET_USER_NICKNAME } from '@/constants/graphql/mutations'
import { GET_USER } from '@/constants/graphql/queries'
import { AuthContext } from '@/stores/auth'
import { Button, FormScrollView, useToast } from '@/components/base'
import { FormInput } from '@/components/form-control'
import { Stack } from '@/components/layout'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import NavigationBar from '@/components/NavigationBar'

type nicknameValues = {
  nickname: string
}

const defaultValues: nicknameValues = {
  nickname: ''
}

export default function NicknameScreen() {
  const { t } = useTranslation()
  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { dirtyFields }
  } = useForm<nicknameValues>({ defaultValues })
  const {
    field: { value: nickname }
  } = useController({ control, name: 'nickname' })
  const user = AuthContext.useSelector((state) => state.context.values?.user)
  const { data } = useQuery(GET_USER, {
    variables: {
      id: user?.id
    }
  })
  const toast = useToast()
  const loading = useLoadingIndicator()

  useEffect(() => {
    if (!dirtyFields.nickname && data?.user) setValue('nickname', data.user.nickname || '')
  }, [data?.user, dirtyFields.nickname, setValue])

  const [setUserNickname] = useMutation(SET_USER_NICKNAME, {
    onCompleted: (data) => {
      loading.stop()
      toast.show({ variant: 'success', description: t('settings:nicknameUpdated') })
    },
    onError: (error) => {
      loading.stop()
      toast.show({ variant: 'warning', description: t('settings:nicknameUpdateFailed') })
      console.log('error', error)
    }
  })

  const onSubmit = async (formValues: nicknameValues) => {
    Keyboard.dismiss()
    const nickname = normalizeNickname(formValues.nickname)
    if (!isNicknameValid(nickname)) {
      setError('nickname', { message: t('settings:nicknameHint') })
      return
    }
    loading.start()
    setUserNickname({
      variables: {
        nickname
      }
    })
  }
  return (
    <>
      <Stack.Screen
        options={{
          header: () => <NavigationBar hasBottomBorder={true}>{t('settings:nickname')}</NavigationBar>
        }}
      />
      <FormScrollView className={'flex-1'} contentContainerStyle={{ flexGrow: 1 }}>
        <View className={'grow bg-background-secondary p-4'}>
          <View className={'gap-4 rounded-lg border border-border-10 bg-background-primary p-4'}>
            <FormInput
              control={control}
              name={'nickname'}
              label={t('settings:nickname')}
              autoCorrect={false}
              returnKeyType={'done'}
              hint={t('settings:nicknameHint')}
            />
            <Button
              size={'md'}
              onPress={handleSubmit(onSubmit)}
              disabled={nickname === data?.user.nickname || nickname === '' || loading.isLoading}
            >
              {t('settings:save')}
            </Button>
          </View>
          <View className={'h-4'} />
        </View>
      </FormScrollView>
    </>
  )
}

function isNicknameValid(nickname: string) {
  if (nickname.length === 0 || countGraphemes(nickname) > 30) {
    return false
  }
  return /^[\p{L}\p{M}\p{N}]+(?: [\p{L}\p{M}\p{N}]+)*$/u.test(nickname)
}

function normalizeNickname(value: string) {
  return value.normalize('NFC').trim()
}

function countGraphemes(value: string) {
  if (typeof Intl.Segmenter === 'function') {
    return Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(value)).length
  }

  return Array.from(value).length
}
